import React, { useState } from 'react';
import { C } from '../constants/theme';
import { useAuth } from '../context/AuthContext';

const inputStyle = {
  width: '100%',
  border: `1.5px solid ${C.border}`,
  borderRadius: 10,
  padding: '12px 14px',
  fontSize: 14,
  color: C.text,
  background: '#FAFAFA',
  boxSizing: 'border-box',
  outline: 'none',
  transition: 'border-color .2s, background .2s',
  fontFamily: 'inherit',
};

const inputFocusStyle = {
  borderColor: C.primary,
  background: C.white,
};

function InputField({ label, type = 'text', value, onChange, placeholder, required }) {
  const [focused, setFocused] = useState(false);
  return (
    <div style={{ marginBottom: 18 }}>
      <label style={{
        display: 'block', fontSize: 12, fontWeight: 700,
        color: C.textMuted, marginBottom: 6,
        textTransform: 'uppercase', letterSpacing: 0.6,
      }}>
        {label}{required && <span style={{ color: C.danger }}> *</span>}
      </label>
      <input
        type={type}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        required={required}
        onFocus={() => setFocused(true)}
        onBlur={() => setFocused(false)}
        style={{ ...inputStyle, ...(focused ? inputFocusStyle : {}) }}
      />
    </div>
  );
}

export function LoginPage() {
  const { login, error, setError } = useAuth();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading]   = useState(false);
  const [showPass, setShowPass] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!username || !password) { setError('Please enter username and password.'); return; }
    setLoading(true);
    await login(username, password);
    setLoading(false);
  };

  return (
    <div style={{
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #1A1340 0%, #2D1B6E 50%, #1A1340 100%)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: 20,
      position: 'relative',
      overflow: 'hidden',
    }}>
      {/* Background decoration */}
      <div style={{
        position: 'absolute', width: 400, height: 400, borderRadius: '50%',
        background: 'rgba(108,63,197,.15)', top: -100, right: -100, pointerEvents: 'none',
      }} />
      <div style={{
        position: 'absolute', width: 300, height: 300, borderRadius: '50%',
        background: 'rgba(108,63,197,.1)', bottom: -80, left: -80, pointerEvents: 'none',
      }} />

      {/* Card */}
      <div style={{
        background: C.white,
        borderRadius: 20,
        width: '100%',
        maxWidth: 420,
        boxShadow: '0 24px 80px rgba(0,0,0,.35)',
        overflow: 'hidden',
        position: 'relative',
        zIndex: 1,
      }}>
        {/* Header strip */}
        <div style={{
          background: `linear-gradient(135deg, ${C.primary}, ${C.primaryDark})`,
          padding: '32px 36px 28px',
          textAlign: 'center',
        }}>
          <div style={{
            width: 60, height: 60, borderRadius: 16,
            background: 'rgba(255,255,255,.2)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: 28, margin: '0 auto 14px',
            backdropFilter: 'blur(10px)',
          }}>
            📡
          </div>
          <h1 style={{ margin: 0, color: C.white, fontSize: 22, fontWeight: 800, letterSpacing: -0.5 }}>
            Feed Provider CMS
          </h1>
          <p style={{ margin: '6px 0 0', color: 'rgba(255,255,255,.7)', fontSize: 13 }}>
            Sign in to your account
          </p>
        </div>

        {/* Form */}
        <div style={{ padding: '32px 36px 36px' }}>
          {error && (
            <div style={{
              background: '#FEF2F2', border: `1px solid #FECACA`,
              borderRadius: 10, padding: '11px 14px', marginBottom: 20,
              display: 'flex', alignItems: 'center', gap: 8,
            }}>
              <span style={{ fontSize: 16 }}>⚠️</span>
              <span style={{ fontSize: 13, color: '#DC2626', fontWeight: 500 }}>{error}</span>
            </div>
          )}

          <form onSubmit={handleSubmit}>
            <InputField
              label="Username / Email"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              placeholder="admin@example.com"
              required
            />

            {/* Password with show/hide */}
            <div style={{ marginBottom: 24 }}>
              <label style={{
                display: 'block', fontSize: 12, fontWeight: 700,
                color: C.textMuted, marginBottom: 6,
                textTransform: 'uppercase', letterSpacing: 0.6,
              }}>
                Password <span style={{ color: C.danger }}>*</span>
              </label>
              <div style={{ position: 'relative' }}>
                <input
                  type={showPass ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  style={{ ...inputStyle, paddingRight: 44 }}
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowPass((s) => !s)}
                  style={{
                    position: 'absolute', right: 12, top: '50%', transform: 'translateY(-50%)',
                    background: 'none', border: 'none', cursor: 'pointer', fontSize: 18,
                    color: C.textMuted, padding: 0, lineHeight: 1,
                  }}
                  title={showPass ? 'Hide password' : 'Show password'}
                >
                  {showPass ? '🙈' : '👁️'}
                </button>
              </div>
            </div>

            <button
              type="submit"
              disabled={loading}
              style={{
                width: '100%',
                background: loading
                  ? '#A78BDA'
                  : `linear-gradient(135deg, ${C.primary}, ${C.primaryDark})`,
                color: C.white,
                border: 'none',
                borderRadius: 10,
                padding: '13px',
                fontSize: 15,
                fontWeight: 700,
                cursor: loading ? 'not-allowed' : 'pointer',
                transition: 'all .2s',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 8,
                fontFamily: 'inherit',
              }}
            >
              {loading ? (
                <>
                  <span style={{
                    width: 18, height: 18, border: '2px solid rgba(255,255,255,.4)',
                    borderTopColor: C.white, borderRadius: '50%',
                    display: 'inline-block',
                    animation: 'spin .7s linear infinite',
                  }} />
                  Signing in…
                </>
              ) : (
                'Sign In'
              )}
            </button>
          </form>

          {/* Demo hint */}
          <div style={{
            marginTop: 24, padding: '12px 14px', background: C.primaryLight,
            borderRadius: 10, textAlign: 'center',
          }}>
            <p style={{ margin: 0, fontSize: 12, color: C.primary, fontWeight: 600 }}>
              Demo credentials
            </p>
            <p style={{ margin: '3px 0 0', fontSize: 12, color: C.primaryDark }}>
              Username: <strong>admin</strong> &nbsp;|&nbsp; Password: <strong>admin123</strong>
            </p>
          </div>
        </div>
      </div>

      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}
